import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dia, shapes } from '@joint/core';
import { Document, DocumentRelationship } from '../../store/api/apiSlice';

interface DocumentRelationshipDiagramProps {
  documents: Document[];
  relationships: DocumentRelationship[];
  selectedDocumentId?: string;
  onDocumentClick?: (document: Document) => void;
  className?: string;
}

interface DiagramNode {
  id: string;
  document: Document;
  x: number;
  y: number;
}

// Calculate layout positions using a simple hierarchical (org chart-like) approach with orthogonal routing
const calculateLayout = (documents: Document[], relationships: DocumentRelationship[]): DiagramNode[] => {
  const idToDoc: Record<string, Document> = Object.fromEntries(documents.map(d => [d.id, d]));
  const inDegree: Record<string, number> = {};
  const children: Record<string, string[]> = {};

  documents.forEach(d => {
    inDegree[d.id] = 0;
    children[d.id] = [];
  });

  relationships.forEach(rel => {
    if (idToDoc[rel.parent_document_id] && idToDoc[rel.child_document_id]) {
      inDegree[rel.child_document_id] = (inDegree[rel.child_document_id] || 0) + 1;
      children[rel.parent_document_id].push(rel.child_document_id);
    }
  });

  const roots = documents
    .filter(d => (inDegree[d.id] || 0) === 0)
    .map(d => d.id);

  // Assign levels via BFS from roots with cycle prevention
  const level: Record<string, number> = {};
  const queue: string[] = [];
  const visited: Set<string> = new Set();
  
  roots.forEach(r => { level[r] = 0; queue.push(r); });
  
  while (queue.length) {
    const cur = queue.shift()!;
    
    // Prevent infinite loops by tracking visited nodes in current BFS path
    if (visited.has(cur)) continue;
    visited.add(cur);
    
    const nextLevel = (level[cur] || 0) + 1;
    children[cur].forEach(ch => {
      // Only update level if it's undefined or we found a longer path
      // and ensure we don't exceed reasonable depth to prevent infinite loops
      if ((level[ch] === undefined || nextLevel > level[ch]) && nextLevel < documents.length) {
        level[ch] = nextLevel;
        queue.push(ch);
      }
    });
  }

  // For any disconnected nodes (no relationships), place at level 0
  documents.forEach(d => {
    if (level[d.id] === undefined) level[d.id] = 0;
  });

  // Group nodes by level and position them in rows
  const levels: Record<number, string[]> = {};
  Object.keys(level).forEach(id => {
    const lv = level[id];
    if (!levels[lv]) levels[lv] = [];
    levels[lv].push(id);
  });

  const nodes: DiagramNode[] = [];
  const rowHeight = 120;
  const colWidth = 200;
  const startX = 100;
  const startY = 80;

  Object.keys(levels).sort((a, b) => Number(a) - Number(b)).forEach((lvStr) => {
    const lv = Number(lvStr);
    const ids = levels[lv];
    ids.forEach((id, idx) => {
      nodes.push({
        id,
        document: idToDoc[id],
        x: startX + idx * colWidth,
        y: startY + lv * rowHeight,
      });
    });
  });

  return nodes;
};

export const DocumentRelationshipDiagram: React.FC<DocumentRelationshipDiagramProps> = ({
  documents,
  relationships,
  selectedDocumentId,
  onDocumentClick,
  className = ''
}) => {
  const paperRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<dia.Graph | null>(null);
  const paperInstanceRef = useRef<dia.Paper | null>(null);
  const [focusedDocumentId, setFocusedDocumentId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; documentId: string } | null>(null);
  const navigate = useNavigate();

  // Memoize filtered data to prevent render loops
  const filteredData = useMemo(() => {
    if (!focusedDocumentId) {
      return {
        relationships,
        documents
      };
    }
    
    // Filter relationships - show only direct relationships (1-hop) from the focused document
    const filteredRels = relationships.filter(rel => 
      rel.parent_document_id === focusedDocumentId || 
      rel.child_document_id === focusedDocumentId
    );
    
    // Always include the focused document, even if it has no relationships
    const connectedDocIds = new Set([focusedDocumentId]);
    filteredRels.forEach(rel => {
      connectedDocIds.add(rel.parent_document_id);
      connectedDocIds.add(rel.child_document_id);
    });
    
    const filteredDocs = documents.filter(doc => connectedDocIds.has(doc.id));
    
    // If no documents are found (shouldn't happen since we include focused), return at least the focused document
    if (filteredDocs.length === 0) {
      const focusedDoc = documents.find(doc => doc.id === focusedDocumentId);
      return {
        relationships: filteredRels,
        documents: focusedDoc ? [focusedDoc] : documents
      };
    }
    
    return {
      relationships: filteredRels,
      documents: filteredDocs
    };
  }, [documents, relationships, focusedDocumentId]);

  // Memoize layout calculation using full data for complete diagram creation
  const layoutNodes = useMemo(() => {
    console.log('Calculating layout for documents:', documents.length);
    return calculateLayout(documents, relationships);
  }, [documents, relationships]);

  // Handle context menu close
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Handle focus on specific document relationships
  const handleFocusDocument = (documentId: string) => {
    setFocusedDocumentId(documentId === focusedDocumentId ? null : documentId);
    setContextMenu(null);
  };

  // Add a debug log for component mount
  useEffect(() => {
    console.log('DocumentRelationshipDiagram component mounted');
    return () => {
      console.log('DocumentRelationshipDiagram component unmounted');
    };
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        handleContextMenuClose();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  // Get node color based on contract status
  const getNodeColor = (contractStatus?: string) => {
    switch (contractStatus?.toLowerCase()) {
      case 'active':
        return { border: '#10b981', fill: '#ecfdf5' }; // Green
      case 'draft':
        return { border: '#3b82f6', fill: '#eff6ff' }; // Blue
      case 'terminated':
      case 'expired':
        return { border: '#ef4444', fill: '#fef2f2' }; // Red
      case 'suspended':
        return { border: '#f59e0b', fill: '#fefbeb' }; // Orange
      case 'renewed':
        return { border: '#8b5cf6', fill: '#f5f3ff' }; // Purple
      default:
        return { border: '#6b7280', fill: '#f9fafb' }; // Gray
    }
  };

  // Get edge style based on relationship type
  const getEdgeStyle = (relationshipType: string) => {
    switch (relationshipType.toLowerCase()) {
      case 'governed by':
        return {
          stroke: '#10b981',
          strokeWidth: 3,
          strokeDasharray: 'none',
          targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: '#10b981' }
        };
      case 'amends':
        return {
          stroke: '#f59e0b',
          strokeWidth: 2,
          strokeDasharray: '5,5',
          targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: '#f59e0b' }
        };
      case 'supersedes':
        return {
          stroke: '#ef4444',
          strokeWidth: 3,
          strokeDasharray: 'none',
          targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: '#ef4444' }
        };
      case 'references':
        return {
          stroke: '#6b7280',
          strokeWidth: 1,
          strokeDasharray: '2,3',
          targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: '#6b7280' }
        };
      case 'supplements':
        return {
          stroke: '#8b5cf6',
          strokeWidth: 2,
          strokeDasharray: '10,5',
          targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: '#8b5cf6' }
        };
      default:
        return {
          stroke: '#6b7280',
          strokeWidth: 1,
          strokeDasharray: 'none',
          targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: '#6b7280' }
        };
    }
  };

  useEffect(() => {
    console.log('DocumentRelationshipDiagram received:', {
      layoutNodesCount: layoutNodes.length,
      relationshipsCount: relationships.length,
      layoutNodes: layoutNodes.map(n => ({ id: n.id, title: n.document.title || n.document.original_filename, contract_type: n.document.contract_type })),
      relationships: relationships.map(r => ({ id: r.id, type: r.relationship_type, parent: r.parent_document_id, child: r.child_document_id }))
    });
    
    if (!paperRef.current || layoutNodes.length === 0) {
      console.log('Diagram not rendering:', { paperRefCurrent: !!paperRef.current, layoutNodesLength: layoutNodes.length });
      return;
    }

    console.log('Starting diagram rendering...', { paperRefCurrent: !!paperRef.current, layoutNodesLength: layoutNodes.length });

    // Add a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (!paperRef.current) {
        console.log('paperRef.current is null after timeout');
        return;
      }

      try {
        // Clear previous diagram
        if (paperInstanceRef.current) {
          console.log('Clearing previous diagram');
          paperInstanceRef.current.remove();
        }

        // Create new graph and paper
        const graph = new dia.Graph();
        const paper = new dia.Paper({
          el: paperRef.current,
          model: graph,
          width: 1200, // Reasonable canvas size for panning
          height: 900,
          gridSize: 10,
          drawGrid: true,
          background: {
            color: '#f8fafc'
          },
          interactive: {
            elementMove: true,
            linkMove: false
          },
          // Enable panning and zooming
          restrictTranslate: false,
          multiLinks: true,
          linkPinning: false
        });

        // Add zoom functionality
        paper.on('blank:mousewheel', (evt, x, y, delta) => {
          evt.preventDefault();
          const scale = paper.scale();
          const newScale = Math.max(0.2, Math.min(3, scale.sx + delta * 0.1));
          
          // Zoom centered on mouse position
          paper.scale(newScale, newScale);
        });

        // Add panning functionality
        let isDragging = false;
        let dragStart = { x: 0, y: 0 };
        
        paper.on('blank:pointerdown', (evt) => {
          isDragging = true;
          dragStart = { x: evt.clientX || 0, y: evt.clientY || 0 };
          paperRef.current!.style.cursor = 'grabbing';
        });

        paper.on('blank:pointermove', (evt) => {
          if (isDragging) {
            const dx = (evt.clientX || 0) - dragStart.x;
            const dy = (evt.clientY || 0) - dragStart.y;
            const currentTranslate = paper.translate();
            paper.translate(currentTranslate.tx + dx, currentTranslate.ty + dy);
            dragStart = { x: evt.clientX || 0, y: evt.clientY || 0 };
          }
        });

        paper.on('blank:pointerup', () => {
          isDragging = false;
          paperRef.current!.style.cursor = 'grab';
        });

        // Set initial cursor
        paperRef.current.style.cursor = 'grab';

        console.log('Graph and paper created');

        graphRef.current = graph;
        paperInstanceRef.current = paper;

        // Use memoized layout calculation
        console.log('Using memoized layout nodes:', layoutNodes.length);

        // Create document nodes
        const nodeElements: { [key: string]: shapes.standard.Rectangle } = {};
        
        layoutNodes.forEach(node => {
          const colors = getNodeColor(node.document.contract_status);
          const isSelected = selectedDocumentId === node.id;
          
          const rect = new shapes.standard.Rectangle({
            position: { x: node.x - 75, y: node.y - 30 },
            size: { width: 150, height: 60 },
            attrs: {
              body: {
                fill: colors.fill,
                stroke: isSelected ? '#000' : colors.border,
                strokeWidth: isSelected ? 3 : 2,
                rx: 8,
                ry: 8,
                cursor: 'pointer'
              },
              label: {
                text: node.document.title || node.document.original_filename,
                fontSize: 11,
                fontWeight: 'bold',
                fill: '#1f2937',
                textWrap: {
                  width: 140,
                  height: 50,
                  ellipsis: true
                }
              }
            }
          });

          console.log('Created rectangle for node:', { 
            id: node.id, 
            title: node.document.title || node.document.original_filename,
            position: { x: node.x - 75, y: node.y - 30 },
            colors 
          });

          // Simplify - remove complex statusLabel for now
          // Add contract type and status as additional text
          // if (node.document.contract_type || node.document.contract_status) {
          //   const statusText = [
          //     node.document.contract_type,
          //     node.document.contract_status
          //   ].filter(Boolean).join(' • ');

          //   rect.attr('statusLabel', {
          //     text: statusText,
          //     fontSize: 9,
          //     fill: '#6b7280',
          //     refY: 40,
          //     refX: '50%',
          //     textAnchor: 'middle'
          //   });
          // }

          rect.set('documentId', node.id);
          nodeElements[node.id] = rect;
          graph.addCell(rect);
          console.log('Added rectangle to graph for:', node.id);
        });

        console.log('All nodes added to graph, nodeElements:', Object.keys(nodeElements));

        // Create relationship links (create all, filter visibility later)
        relationships.forEach(rel => {
          // Reverse direction so arrows point from child to parent
          const sourceElement = nodeElements[rel.child_document_id];
          const targetElement = nodeElements[rel.parent_document_id];
          
          if (sourceElement && targetElement) {
            const edgeStyle = getEdgeStyle(rel.relationship_type);
            
            const link = new shapes.standard.Link({
              source: { id: sourceElement.id },
              target: { id: targetElement.id },
              relationshipId: rel.id, // Store relationship ID for filtering
              attrs: {
                line: {
                  stroke: edgeStyle.stroke,
                  strokeWidth: edgeStyle.strokeWidth,
                  strokeDasharray: edgeStyle.strokeDasharray,
                  targetMarker: edgeStyle.targetMarker
                }
              },
              labels: [{
                markup: [
                  {
                    tagName: 'rect',
                    selector: 'labelBody'
                  },
                  {
                    tagName: 'text',
                    selector: 'labelText'
                  }
                ],
                attrs: {
                  labelText: {
                    text: rel.relationship_type,
                    fontSize: 10,
                    fill: '#374151',
                    textAnchor: 'middle',
                    textVerticalAnchor: 'middle'
                  },
                  labelBody: {
                    fill: 'white',
                    stroke: edgeStyle.stroke,
                    strokeWidth: 1,
                    rx: 3,
                    ry: 3,
                    refWidth: '100%',
                    refHeight: '100%',
                    refX: '-50%',
                    refY: '-50%'
                  }
                },
                position: {
                  distance: 0.5
                }
              }]
            });

            // Use orthogonal router and rounded connectors for elbowed links
            // @ts-ignore
            if (typeof (link as any).router === 'function') {
              (link as any).router('orthogonal');
            }
            // @ts-ignore
            if (typeof (link as any).connector === 'function') {
              (link as any).connector('rounded');
            }

            graph.addCell(link);
            console.log('Added link to graph for relationship:', rel.id);
          } else {
            console.log('Could not create link - missing elements:', { 
              relationshipId: rel.id,
              parentId: rel.parent_document_id,
              childId: rel.child_document_id,
              sourceElement: !!sourceElement,
              targetElement: !!targetElement
            });
          }
        });

        console.log('Graph setup complete, total cells:', graph.getCells().length);

        // Center the diagram content
        if (graph.getCells().length > 0) {
          const bbox = graph.getBBox();
          if (bbox) {
            // Calculate center offset to position content in viewport
            const viewportWidth = paperRef.current.clientWidth;
            const viewportHeight = paperRef.current.clientHeight;
            const offsetX = Math.max(0, (viewportWidth - bbox.width) / 2 - bbox.x);
            const offsetY = Math.max(50, (viewportHeight - bbox.height) / 2 - bbox.y);
            paper.translate(offsetX, offsetY);
          }
        }

        // Handle left clicks - navigate to document detail
        paper.on('element:pointerclick', (elementView) => {
          const documentId = elementView.model.get('documentId');
          if (documentId) {
            // Navigate to document detail page
            navigate(`/documents/${documentId}`);
            
            // Also call the onDocumentClick prop if provided
            if (onDocumentClick) {
              const documentNode = layoutNodes.find(n => n.id === documentId);
              if (documentNode) {
                onDocumentClick(documentNode.document);
              }
            }
          }
        });

        // Handle right clicks - show context menu
        paper.on('element:contextmenu', (elementView, evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          const documentId = elementView.model.get('documentId');
          if (documentId) {
            // Use page coordinates directly for proper positioning
            setContextMenu({
              x: evt.pageX || evt.clientX || 0,
              y: evt.pageY || evt.clientY || 0,
              documentId: documentId
            });
          }
        });

        // Prevent browser context menu on empty areas of the diagram
        paper.on('blank:contextmenu', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
        });
      } catch (error) {
        console.error('Error setting up diagram:', error);
      }
    }, 100); // 100ms delay

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (paperInstanceRef.current) {
        paperInstanceRef.current.remove();
      }
    };
  }, [layoutNodes, relationships, selectedDocumentId, navigate, onDocumentClick]);

  // Handle focus changes by showing/hiding elements instead of rebuilding
  useEffect(() => {
    if (!paperInstanceRef.current || !graphRef.current) return;

    console.log('Visibility filtering triggered, focusedDocumentId:', focusedDocumentId);
    
    const graph = graphRef.current;
    const allCells = graph.getCells();
    
    console.log('Total cells in graph:', allCells.length);
    
    if (!focusedDocumentId) {
      // Show all elements
      console.log('Showing all elements');
      allCells.forEach(cell => {
        if (cell.get('type') === 'standard.Rectangle') {
          const attrs = cell.get('attrs') || {};
          cell.set('attrs', {
            ...attrs,
            body: { ...(attrs.body || {}), opacity: 1 },
            label: { ...(attrs.label || {}), opacity: 1 }
          });
        } else if (cell.get('type') === 'standard.Link') {
          const attrs = cell.get('attrs') || {};
          cell.set('attrs', {
            ...attrs,
            line: { ...(attrs.line || {}), opacity: 1 },
            wrapper: { ...(attrs.wrapper || {}), opacity: 1 }
          });
        }
      });
    } else {
      // Calculate filtered data inline
      const filteredRels = relationships.filter(rel => 
        rel.parent_document_id === focusedDocumentId || 
        rel.child_document_id === focusedDocumentId
      );
      
      const connectedDocIds = new Set([focusedDocumentId]);
      filteredRels.forEach(rel => {
        connectedDocIds.add(rel.parent_document_id);
        connectedDocIds.add(rel.child_document_id);
      });
      
      const filteredDocs = documents.filter(doc => connectedDocIds.has(doc.id));
      
      const filteredRelIds = filteredRels.map(rel => rel.id);
      const filteredDocIds = filteredDocs.map(doc => doc.id);
      
      console.log('Filtered relationships:', filteredRelIds);
      console.log('Filtered documents:', filteredDocIds);
      
      let visibleNodes = 0;
      let visibleLinks = 0;
      let hiddenNodes = 0;
      let hiddenLinks = 0;
      
      allCells.forEach(cell => {
        if (cell.get('type') === 'standard.Rectangle') {
          // Hide/show document nodes
          const docId = cell.get('documentId');
          const shouldShow = filteredDocIds.includes(docId);
          const attrs = cell.get('attrs') || {};
          
          if (shouldShow) {
            cell.set('attrs', {
              ...attrs,
              body: { ...(attrs.body || {}), opacity: 1 },
              label: { ...(attrs.label || {}), opacity: 1 }
            });
            visibleNodes++;
          } else {
            cell.set('attrs', {
              ...attrs,
              body: { ...(attrs.body || {}), opacity: 0.1 },
              label: { ...(attrs.label || {}), opacity: 0.1 }
            });
            hiddenNodes++;
          }
          console.log(`Node ${docId}: ${shouldShow ? 'visible' : 'hidden'}`);
        } else if (cell.get('type') === 'standard.Link') {
          // Hide/show relationship links
          const relId = cell.get('relationshipId');
          const shouldShow = filteredRelIds.includes(relId);
          const attrs = cell.get('attrs') || {};
          
          if (shouldShow) {
            cell.set('attrs', {
              ...attrs,
              line: { ...(attrs.line || {}), opacity: 1 },
              wrapper: { ...(attrs.wrapper || {}), opacity: 1 }
            });
            visibleLinks++;
          } else {
            cell.set('attrs', {
              ...attrs,
              line: { ...(attrs.line || {}), opacity: 0.1 },
              wrapper: { ...(attrs.wrapper || {}), opacity: 0.1 }
            });
            hiddenLinks++;
          }
          console.log(`Link ${relId}: ${shouldShow ? 'visible' : 'hidden'}`);
        }
      });
      
      console.log(`Visibility result: ${visibleNodes} nodes visible, ${hiddenNodes} hidden, ${visibleLinks} links visible, ${hiddenLinks} hidden`);
    }
    
    // Force paper to update
    paperInstanceRef.current.updateViews();
  }, [focusedDocumentId, documents, relationships]);
  
  if (documents.length === 0) {
    return (
      <div className={`flex items-center justify-center h-96 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 ${className}`}>
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No documents to display
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Select a vendor or adjust filters to view contract relationships
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={paperRef} 
        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mx-auto"
        style={{ width: '800px', height: '600px' }}
      />
      
      {/* Zoom Controls */}
      <div className="absolute top-4 left-4 flex flex-col space-y-2">
        <button
          onClick={() => {
            if (paperInstanceRef.current) {
              const scale = paperInstanceRef.current.scale();
              const newScale = Math.min(3, scale.sx + 0.2);
              paperInstanceRef.current.scale(newScale, newScale);
            }
          }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 hover:bg-gray-50 dark:hover:bg-gray-700 shadow"
          title="Zoom In"
        >
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
        <button
          onClick={() => {
            if (paperInstanceRef.current) {
              const scale = paperInstanceRef.current.scale();
              const newScale = Math.max(0.2, scale.sx - 0.2);
              paperInstanceRef.current.scale(newScale, newScale);
            }
          }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 hover:bg-gray-50 dark:hover:bg-gray-700 shadow"
          title="Zoom Out"
        >
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <button
          onClick={() => {
            if (paperInstanceRef.current && graphRef.current) {
              // Reset zoom and center content
              paperInstanceRef.current.scale(1, 1);
              const bbox = graphRef.current.getBBox();
              if (bbox && paperRef.current) {
                const viewportWidth = paperRef.current.clientWidth;
                const viewportHeight = paperRef.current.clientHeight;
                const offsetX = Math.max(0, (viewportWidth - bbox.width) / 2 - bbox.x);
                const offsetY = Math.max(50, (viewportHeight - bbox.height) / 2 - bbox.y);
                paperInstanceRef.current.translate(offsetX, offsetY);
              }
            }
          }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 hover:bg-gray-50 dark:hover:bg-gray-700 shadow"
          title="Reset View"
        >
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
      
      {/* Legend */}
      <div className="absolute top-4 right-4 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Contract Status</h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-green-500 bg-green-50 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Active</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-500 bg-blue-50 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Draft</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-red-500 bg-red-50 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Terminated</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-orange-500 bg-orange-50 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Suspended</span>
          </div>
        </div>
        
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-4">Relationships</h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-0.5 bg-green-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Governed By</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-0.5 bg-orange-500 border-dashed border-t-2 border-orange-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Amends</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-0.5 bg-red-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Supersedes</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-0.5 bg-gray-500 opacity-50 border-dotted border-t-2 border-gray-500"></div>
            <span className="text-gray-700 dark:text-gray-300">References</span>
          </div>
        </div>
      </div>

      {/* Focus Mode Indicator */}
      {focusedDocumentId && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-sm font-medium">Focused View</span>
            <button
              onClick={() => setFocusedDocumentId(null)}
              className="ml-2 text-white hover:text-gray-200"
              title="Exit Focus Mode"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => {
              navigate(`/documents/${contextMenu.documentId}`);
              handleContextMenuClose();
            }}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>View Contract Details</span>
            </div>
          </button>
          <button
            className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => handleFocusDocument(contextMenu.documentId)}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>
                {contextMenu.documentId === focusedDocumentId ? 'Exit Focus Mode' : 'View Focused Relationships'}
              </span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}; 